import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Card, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Divider, TextField, MenuItem, Checkbox, FormControlLabel, LinearProgress, IconButton, Paper, InputAdornment } from '@mui/material';
import { Icon } from '@iconify/react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
// --- MOCK DATA ---
const RATE_CARDS = [{
  pair: 'USD/GHS',
  flag: '🇬🇭',
  bid: '13.45',
  ask: '13.50',
  spread: '0.05',
  spreadPct: '0.37%',
  change: '+0.5%',
  changeDir: 'up',
  updated: '14:30:00 GMT'
}, {
  pair: 'USD/XOF',
  flag: '🇸🇳',
  bid: '610.25',
  ask: '611.40',
  spread: '1.15',
  spreadPct: '0.19%',
  change: '-0.2%',
  changeDir: 'down',
  updated: '14:30:00 GMT'
}, {
  pair: 'EUR/USD',
  flag: '🇪🇺',
  bid: '1.0850',
  ask: '1.0855',
  spread: '0.0005',
  spreadPct: '0.04%',
  change: '+0.1%',
  changeDir: 'up',
  updated: '14:30:00 GMT'
}, {
  pair: 'GBP/USD',
  flag: '🇬🇧',
  bid: '1.2750',
  ask: '1.2755',
  spread: '0.0005',
  spreadPct: '0.03%',
  change: '+0.3%',
  changeDir: 'up',
  updated: '14:30:00 GMT'
}, {
  pair: 'EUR/GHS',
  flag: '🇬🇭',
  bid: '14.58',
  ask: '14.64',
  spread: '0.06',
  spreadPct: '0.41%',
  change: '+0.4%',
  changeDir: 'up',
  updated: '14:30:00 GMT'
}];
const PARTNER_MARGINS = [{
  id: 1,
  partner: 'Bank Ghana Ltd',
  corridor: 'Ghana-UK',
  base: '13.48',
  margin: '2.0',
  effective: '13.74',
  lastChanged: '2025-01-01 06:00 UTC',
  changedBy: 'Sarah Johnson, Ecobank Treasury'
}, {
  id: 2,
  partner: 'PSP Fintech',
  corridor: 'Nigeria-US',
  base: '1.035',
  margin: '1.5',
  effective: '1.050',
  lastChanged: '2024-12-31 18:00 UTC',
  changedBy: 'System'
}, {
  id: 3,
  partner: 'Orange Money Senegal',
  corridor: 'Ghana-Senegal',
  base: '610.25',
  margin: '2.5',
  effective: '625.81',
  lastChanged: '2024-12-28 10:00 UTC',
  changedBy: 'Michael Osei, Ecobank Treasury'
}];
const FX_CONVERSIONS = [{
  id: 1,
  date: '2025-01-01 14:35:22 UTC',
  pair: 'USD/GHS',
  amountFrom: '$500.00',
  amountTo: 'GHS 6,740',
  rate: '13.48',
  margin: '2.0%',
  fee: '$2.50',
  status: 'Completed'
}, {
  id: 2,
  date: '2025-01-01 14:28:45 UTC',
  pair: 'EUR/USD',
  amountFrom: '€1,000',
  amountTo: '$1,085',
  rate: '1.085',
  margin: '1.2%',
  fee: '$5.00',
  status: 'Completed'
}, {
  id: 3,
  date: '2025-01-01 13:55:10 UTC',
  pair: 'USD/XOF',
  amountFrom: '$2,500',
  amountTo: 'XOF 1,525,625',
  rate: '610.25',
  margin: '2.5%',
  fee: '$12.50',
  status: 'Completed'
}];
const EXPOSURE_PIE_DATA = [{
  name: 'USD',
  value: 40,
  color: '#0052CC'
}, {
  name: 'GHS',
  value: 25,
  color: '#0B7923'
}, {
  name: 'XOF',
  value: 20,
  color: '#B38600'
}, {
  name: 'EUR',
  value: 10,
  color: '#A78BFA'
}, {
  name: 'Other',
  value: 5,
  color: '#6B7280'
}];
const EXPOSURE_BAR_DATA = [{
  name: 'Ghana-UK',
  exposure: 1200000,
  fill: '#0052CC'
}, {
  name: 'Nigeria-US',
  exposure: 2500000,
  fill: '#0B7923'
}, {
  name: 'Ghana-Senegal',
  exposure: 800000,
  fill: '#B38600'
}, {
  name: 'Senegal-France',
  exposure: 1500000,
  fill: '#A78BFA'
}];
const PNL_DATA = [{
  corridor: 'Ghana-UK',
  txns: 12,
  volume: '$45,000',
  avgRate: '13.48',
  gainLoss: '+$1,250',
  gainLossPct: '+2.8%',
  isPositive: true
}, {
  corridor: 'Nigeria-US',
  txns: 8,
  volume: '$28,000',
  avgRate: '1.035',
  gainLoss: '-$450',
  gainLossPct: '-1.6%',
  isPositive: false
}, {
  corridor: 'Ghana-Senegal',
  txns: 5,
  volume: '$12,430',
  avgRate: '610.25',
  gainLoss: '+$780',
  gainLossPct: '+6.3%',
  isPositive: true
}];
const RATE_HISTORY_DATA = Array.from({
  length: 30
}).map((_, i) => ({
  date: `2024-12-${(i + 1).toString().padStart(2, '0')}`,
  rate: 13.0 + Math.random() * 0.8
}));
// --- COMPONENT ---
export default function FXManagement({
  onLogout
}: {
  onLogout: () => void;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Dialog states
  const [bulkFxOpen, setBulkFxOpen] = useState(false);
  const [rateHistoryOpen, setRateHistoryOpen] = useState(false);
  const [bulkMarginOpen, setBulkMarginOpen] = useState(false);
  // Inline edit states
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editMarginValue, setEditMarginValue] = useState('');
  const handleEditClick = (id: number, currentMargin: string) => {
    setEditingRowId(id);
    setEditMarginValue(currentMargin);
  };
  const handleSaveEdit = () => {
    // In a real app, save to backend here
    setEditingRowId(null);
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: '#F8F9FA'
  }} data-id="element-4330">
      <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} onLogout={onLogout} data-id="element-4331" />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePage="fx-management" data-id="element-4332" />

      <Box component="main" sx={{
      flexGrow: 1,
      py: 2,
      px: 1,
      width: {
        sm: `calc(100% - 240px)`
      },
      ml: 0,
      mt: 8
    }} data-id="element-4333">
        <Container maxWidth="xl" data-id="element-4334">
          <Grid container spacing={3} data-id="element-4335">
            {/* LEFT MAIN CONTENT */}
            <Grid item xs={12} lg={9} data-id="element-4336">
              {/* RATE BOARD */}
              <Box mb={4} data-id="element-4337">
                <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2} data-id="element-4338">
                  <Box data-id="element-4339">
                    <Typography variant="h5" fontWeight={700} color="#1F2937" data-id="element-4340">
                      Daily FX Fixing Rate
                    </Typography>
                    <Typography variant="body2" color="#6B7280" mt={0.5} data-id="element-4341">
                      Last Updated: 14:30 GMT
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2} data-id="element-4342">
                    <Button variant="outlined" sx={{
                    borderColor: '#0052CC',
                    color: '#0052CC',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '6px'
                  }} startIcon={<Icon icon="lucide:download" data-id="element-4344" />} data-id="element-4343">
                      Ingest Daily Fixing
                    </Button>
                    <Button variant="contained" sx={{
                    bgcolor: '#0B7923',
                    '&:hover': {
                      bgcolor: '#09631c'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '6px',
                    boxShadow: 'none'
                  }} startIcon={<Icon icon="lucide:send" data-id="element-4346" />} data-id="element-4345">
                      Publish Rates to Partners
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2} data-id="element-4347">
                  {RATE_CARDS.map((card, i) => <Grid item xs={12} sm={6} md={4} key={i} data-id="element-4348">
                      <Card elevation={0} sx={{
                    bgcolor: '#F3F5F7',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    p: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }
                  }} data-id="element-4349">
                        <Box display="flex" justifyContent="space-between" mb={1.5} data-id="element-4350">
                          <Typography variant="subtitle2" fontWeight={700} color="#1F2937" data-id="element-4351">
                            {card.flag} {card.pair}
                          </Typography>
                          <Typography variant="caption" color={card.changeDir === 'up' ? '#0B7923' : '#AE2A19'} fontWeight={600} display="flex" alignItems="center" gap={0.5} data-id="element-4352">
                            <Icon icon={card.changeDir === 'up' ? 'lucide:arrow-up' : 'lucide:arrow-down'} width={12} data-id="element-4353" />
                            {card.change}
                          </Typography>
                        </Box>
                        <Grid container spacing={2} mb={1.5} data-id="element-4354">
                          <Grid item xs={6} data-id="element-4355">
                            <Typography variant="caption" color="#6B7280" data-id="element-4356">
                              Bid
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="#1F2937" lineHeight={1.2} data-id="element-4357">
                              {card.bid}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} data-id="element-4358">
                            <Typography variant="caption" color="#6B7280" data-id="element-4359">
                              Ask
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="#1F2937" lineHeight={1.2} data-id="element-4360">
                              {card.ask}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Box display="flex" justifyContent="space-between" alignItems="center" data-id="element-4361">
                          <Typography variant="caption" color="#6B7280" data-id="element-4362">
                            Spread: {card.spread} ({card.spreadPct})
                          </Typography>
                          <Typography variant="caption" color="#6B7280" sx={{
                        fontSize: '10px'
                      }} data-id="element-4363">
                            {card.updated}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>)}
                </Grid>
              </Box>

              {/* TABS SECTION */}
              <Box sx={{
              borderBottom: 1,
              borderColor: '#E5E7EB',
              mb: 3
            }} data-id="element-4364">
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#6B7280',
                  fontSize: '14px',
                  minWidth: 'auto',
                  px: 3
                },
                '& .Mui-selected': {
                  color: '#0052CC !important'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#0052CC'
                }
              }} data-id="element-4365">
                  <Tab label="Partner Margins" data-id="element-4366" />
                  <Tab label="FX Conversions Log" data-id="element-4367" />
                  <Tab label="Exposure & Analytics" data-id="element-4368" />
                </Tabs>
              </Box>

              {/* TAB 1: PARTNER MARGINS */}
              {activeTab === 0 && <Box data-id="element-4369">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} data-id="element-4370">
                    <Box data-id="element-4371">
                      <Typography variant="h6" fontWeight={700} color="#1F2937" data-id="element-4372">
                        Partner-Specific Margin Configuration
                      </Typography>
                      <Typography variant="caption" color="#6B7280" data-id="element-4373">
                        Manage margins per partner and corridor
                      </Typography>
                    </Box>
                    <Button variant="contained" sx={{
                  bgcolor: '#0052CC',
                  '&:hover': {
                    bgcolor: '#004099'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: 'none'
                }} onClick={() => setBulkMarginOpen(true)} data-id="element-4374">
                      Update Corridor Margins
                    </Button>
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px'
              }} data-id="element-4375">
                    <Table size="small" data-id="element-4376">
                      <TableHead sx={{
                    bgcolor: '#F3F5F7'
                  }} data-id="element-4377">
                        <TableRow data-id="element-4378">
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4379">
                            Partner Name
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4380">
                            Corridor
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4381">
                            Base Rate
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4382">
                            Applied Margin %
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4383">
                            Effective Rate
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4384">
                            Last Changed
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4385">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-4386">
                        {PARTNER_MARGINS.map(row => <TableRow key={row.id} data-id="element-4387">
                            <TableCell sx={{
                        color: '#1F2937'
                      }} data-id="element-4388">
                              {row.partner}
                            </TableCell>
                            <TableCell sx={{
                        color: '#6B7280'
                      }} data-id="element-4389">
                              {row.corridor}
                            </TableCell>
                            <TableCell sx={{
                        fontFamily: 'monospace'
                      }} data-id="element-4390">
                              {row.base}
                            </TableCell>
                            <TableCell data-id="element-4391">
                              {editingRowId === row.id ? <Box display="flex" alignItems="center" gap={1} data-id="element-4392">
                                  <TextField size="small" value={editMarginValue} onChange={e => setEditMarginValue(e.target.value)} sx={{
                            width: '80px'
                          }} InputProps={{
                            endAdornment: <InputAdornment position="end" data-id="element-4394">
                                          %
                                        </InputAdornment>
                          }} data-id="element-4393" />
                                  <IconButton size="small" color="success" onClick={handleSaveEdit} data-id="element-4395">
                                    <Icon icon="lucide:check" data-id="element-4396" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => setEditingRowId(null)} data-id="element-4397">
                                    <Icon icon="lucide:x" data-id="element-4398" />
                                  </IconButton>
                                </Box> : <Typography fontWeight={600} color="#0B7923" data-id="element-4399">
                                  {row.margin}%
                                </Typography>}
                            </TableCell>
                            <TableCell sx={{
                        fontFamily: 'monospace'
                      }} data-id="element-4400">
                              {row.effective}
                            </TableCell>
                            <TableCell data-id="element-4401">
                              <Typography variant="body2" color="#1F2937" data-id="element-4402">
                                {row.lastChanged}
                              </Typography>
                              <Typography variant="caption" color="#6B7280" data-id="element-4403">
                                {row.changedBy}
                              </Typography>
                            </TableCell>
                            <TableCell data-id="element-4404">
                              <Button size="small" sx={{
                          textTransform: 'none',
                          minWidth: 'auto'
                        }} onClick={() => handleEditClick(row.id, row.margin)} data-id="element-4405">
                                Edit
                              </Button>
                              <Button size="small" sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          color: '#6B7280'
                        }} data-id="element-4406">
                                History
                              </Button>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>}

              {/* TAB 2: FX CONVERSIONS LOG */}
              {activeTab === 1 && <Box data-id="element-4407">
                  <Box mb={2} data-id="element-4408">
                    <Typography variant="h6" fontWeight={700} color="#1F2937" data-id="element-4409">
                      FX Conversions Processed Today
                    </Typography>
                    <Typography variant="caption" color="#6B7280" data-id="element-4410">
                      All foreign exchange transactions processed
                    </Typography>
                  </Box>

                  <Box display="flex" gap={2} mb={2} data-id="element-4411">
                    <TextField size="small" type="date" label="Start Date" InputLabelProps={{
                  shrink: true
                }} data-id="element-4412" />
                    <TextField size="small" type="date" label="End Date" InputLabelProps={{
                  shrink: true
                }} data-id="element-4413" />
                    <TextField size="small" select label="Currency Pair" value="all" sx={{
                  minWidth: 150
                }} data-id="element-4414">
                      <MenuItem value="all" data-id="element-4415">All Pairs</MenuItem>
                      <MenuItem value="usdghs" data-id="element-4416">USD/GHS</MenuItem>
                      <MenuItem value="eurusd" data-id="element-4417">EUR/USD</MenuItem>
                    </TextField>
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px'
              }} data-id="element-4418">
                    <Table size="small" data-id="element-4419">
                      <TableHead sx={{
                    bgcolor: '#F3F5F7'
                  }} data-id="element-4420">
                        <TableRow data-id="element-4421">
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4422">
                            Date/Time
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4423">
                            Pair
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4424">
                            Amount From
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4425">
                            Amount To
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4426">
                            Rate Applied
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4427">
                            Margin %
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4428">
                            Fee
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4429">
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-4430">
                        {FX_CONVERSIONS.map(row => <TableRow key={row.id} data-id="element-4431">
                            <TableCell sx={{
                        color: '#6B7280',
                        fontSize: '12px'
                      }} data-id="element-4432">
                              {row.date}
                            </TableCell>
                            <TableCell sx={{
                        fontWeight: 600,
                        color: '#1F2937'
                      }} data-id="element-4433">
                              {row.pair}
                            </TableCell>
                            <TableCell sx={{
                        color: '#0B7923'
                      }} data-id="element-4434">
                              {row.amountFrom}
                            </TableCell>
                            <TableCell sx={{
                        color: '#0B7923'
                      }} data-id="element-4435">
                              {row.amountTo}
                            </TableCell>
                            <TableCell sx={{
                        fontFamily: 'monospace'
                      }} data-id="element-4436">
                              {row.rate}
                            </TableCell>
                            <TableCell sx={{
                        color: '#0B7923'
                      }} data-id="element-4437">
                              {row.margin}
                            </TableCell>
                            <TableCell sx={{
                        color: '#AE2A19'
                      }} data-id="element-4438">
                              {row.fee}
                            </TableCell>
                            <TableCell data-id="element-4439">
                              <Box display="flex" alignItems="center" gap={0.5} data-id="element-4440">
                                <Icon icon="lucide:check-circle-2" color="#0B7923" width={14} data-id="element-4441" />
                                <Typography variant="caption" color="#0B7923" data-id="element-4442">
                                  {row.status}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={2} bgcolor="#F8F9FA" borderRadius="8px" border="1px solid #E5E7EB" data-id="element-4443">
                    <Box display="flex" gap={4} data-id="element-4444">
                      <Typography variant="body2" color="#1F2937" data-id="element-4445">
                        Total Conversions Today:{' '}
                        <Box component="span" fontWeight={700} data-id="element-4446">
                          47
                        </Box>
                      </Typography>
                      <Typography variant="body2" color="#1F2937" data-id="element-4447">
                        Total Volume:{' '}
                        <Box component="span" fontWeight={700} data-id="element-4448">
                          $125,430
                        </Box>
                      </Typography>
                      <Typography variant="body2" color="#1F2937" data-id="element-4449">
                        Average Margin:{' '}
                        <Box component="span" fontWeight={700} data-id="element-4450">
                          2.1%
                        </Box>
                      </Typography>
                      <Typography variant="body2" color="#0B7923" fontWeight={600} data-id="element-4451">
                        Total Fees Collected: $2,825
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="#6B7280" data-id="element-4452">
                      Showing 1-20 of 847 conversions | &lt; Previous | Next
                      &gt;
                    </Typography>
                  </Box>
                </Box>}

              {/* TAB 3: EXPOSURE & ANALYTICS */}
              {activeTab === 2 && <Box data-id="element-4453">
                  <Typography variant="h6" fontWeight={700} color="#1F2937" mb={2} data-id="element-4454">
                    FX Exposure & Performance Analysis
                  </Typography>

                  <Grid container spacing={3} mb={4} data-id="element-4455">
                    <Grid item xs={12} md={6} data-id="element-4456">
                      <Box sx={{
                    bgcolor: '#F3F5F7',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    p: 3,
                    height: '350px',
                    display: 'flex',
                    flexDirection: 'column'
                  }} data-id="element-4457">
                        <Typography variant="subtitle2" fontWeight={600} color="#1F2937" mb={2} data-id="element-4458">
                          Currency Exposure
                        </Typography>
                        <Box sx={{
                      flexGrow: 1,
                      minHeight: 0
                    }} data-id="element-4459">
                          <ResponsiveContainer width="100%" height="100%" data-id="element-4460">
                            <PieChart data-id="element-4461">
                              <Pie data={EXPOSURE_PIE_DATA} cx="50%" cy="45%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value" data-id="element-4462">
                                {EXPOSURE_PIE_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4463" />)}
                              </Pie>
                              <RechartsTooltip data-id="element-4464" />
                              <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-4465" />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} textAlign="center" mt={1} data-id="element-4466">
                          Total Exposure: $6.75M
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6} data-id="element-4467">
                      <Box sx={{
                    bgcolor: '#F3F5F7',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    p: 3,
                    height: '350px',
                    display: 'flex',
                    flexDirection: 'column'
                  }} data-id="element-4468">
                        <Typography variant="subtitle2" fontWeight={600} color="#1F2937" mb={2} data-id="element-4469">
                          Exposure by Corridor (USD)
                        </Typography>
                        <Box sx={{
                      flexGrow: 1,
                      minHeight: 0
                    }} data-id="element-4470">
                          <ResponsiveContainer width="100%" height="100%" data-id="element-4471">
                            <BarChart data={EXPOSURE_BAR_DATA} data-id="element-4472">
                              <XAxis dataKey="name" tick={{
                            fontSize: 10,
                            fill: '#6B7280'
                          }} axisLine={false} tickLine={false} data-id="element-4473" />
                              <YAxis tick={{
                            fontSize: 10,
                            fill: '#6B7280'
                          }} axisLine={false} tickLine={false} tickFormatter={val => `$${val / 1000000}M`} data-id="element-4474" />
                              <RechartsTooltip formatter={(value: number) => `$${(value / 1000000).toFixed(1)}M`} data-id="element-4475" />
                              <Bar dataKey="exposure" radius={[4, 4, 0, 0]} data-id="element-4476">
                                {EXPOSURE_BAR_DATA.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-4477" />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle1" fontWeight={700} color="#1F2937" mb={2} data-id="element-4478">
                    Daily P&L by Corridor
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px'
              }} data-id="element-4479">
                    <Table size="small" data-id="element-4480">
                      <TableHead sx={{
                    bgcolor: '#F3F5F7'
                  }} data-id="element-4481">
                        <TableRow data-id="element-4482">
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4483">
                            Corridor
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4484">
                            Transactions
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4485">
                            Volume (USD)
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4486">
                            Avg Rate Used
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4487">
                            Daily Gain/Loss
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 600,
                        color: '#0052CC'
                      }} data-id="element-4488">
                            % Gain/Loss
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-4489">
                        {PNL_DATA.map((row, i) => <TableRow key={i} data-id="element-4490">
                            <TableCell sx={{
                        fontWeight: 600,
                        color: '#1F2937'
                      }} data-id="element-4491">
                              {row.corridor}
                            </TableCell>
                            <TableCell sx={{
                        color: '#6B7280'
                      }} data-id="element-4492">
                              {row.txns} txns
                            </TableCell>
                            <TableCell sx={{
                        color: '#1F2937'
                      }} data-id="element-4493">
                              {row.volume}
                            </TableCell>
                            <TableCell sx={{
                        fontFamily: 'monospace'
                      }} data-id="element-4494">
                              {row.avgRate}
                            </TableCell>
                            <TableCell sx={{
                        color: row.isPositive ? '#0B7923' : '#AE2A19',
                        fontWeight: 600
                      }} data-id="element-4495">
                              {row.gainLoss}
                            </TableCell>
                            <TableCell sx={{
                        color: row.isPositive ? '#0B7923' : '#AE2A19',
                        fontWeight: 600
                      }} data-id="element-4496">
                              {row.gainLossPct}
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>}

              {/* BOTTOM ACTION BAR */}
              <Box mt={4} pt={3} borderTop="1px solid #E5E7EB" display="flex" gap={2} flexWrap="wrap" data-id="element-4497">
                <Button variant="contained" sx={{
                bgcolor: '#F3F5F7',
                color: '#6B7280',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#E5E7EB',
                  boxShadow: 'none'
                }
              }} onClick={() => setRateHistoryOpen(true)} data-id="element-4498">
                  View Rate History
                </Button>
                <Button variant="contained" sx={{
                bgcolor: '#F3F5F7',
                color: '#6B7280',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#E5E7EB',
                  boxShadow: 'none'
                }
              }} data-id="element-4499">
                  Download FX Settlement File
                </Button>
                <Button variant="contained" sx={{
                bgcolor: '#F3F5F7',
                color: '#6B7280',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#E5E7EB',
                  boxShadow: 'none'
                }
              }} data-id="element-4500">
                  Generate FX Report
                </Button>
                <Box flexGrow={1} data-id="element-4501" />
                <Button variant="contained" sx={{
                bgcolor: '#0052CC',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#004099',
                  boxShadow: 'none'
                }
              }} onClick={() => setBulkFxOpen(true)} data-id="element-4502">
                  Schedule Bulk FX
                </Button>
              </Box>
            </Grid>

            {/* RIGHT SIDEBAR - REAL-TIME STATUS */}
            <Grid item xs={12} lg={3} data-id="element-4503">
              <Box sx={{
              bgcolor: '#F3F5F7',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              p: 3,
              position: 'sticky',
              top: '80px'
            }} data-id="element-4504">
                <Typography variant="subtitle1" fontWeight={700} color="#1F2937" mb={3} data-id="element-4505">
                  System Status
                </Typography>

                <Box mb={3} data-id="element-4506">
                  <Box display="flex" alignItems="flex-start" gap={1.5} mb={2} data-id="element-4507">
                    <Icon icon="lucide:clock" width={18} color="#6B7280" style={{
                    marginTop: 2
                  }} data-id="element-4508" />
                    <Box data-id="element-4509">
                      <Typography variant="caption" color="#6B7280" display="block" data-id="element-4510">
                        Current Rate Update Time
                      </Typography>
                      <Typography variant="body2" color="#1F2937" fontWeight={600} data-id="element-4511">
                        2025-01-01 14:30:00 GMT
                      </Typography>
                      <Typography variant="caption" color="#6B7280" data-id="element-4512">
                        (30 minutes ago)
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start" gap={1.5} mb={2} data-id="element-4513">
                    <Icon icon="lucide:calendar" width={18} color="#6B7280" style={{
                    marginTop: 2
                  }} data-id="element-4514" />
                    <Box data-id="element-4515">
                      <Typography variant="caption" color="#6B7280" display="block" data-id="element-4516">
                        Next Rate Update
                      </Typography>
                      <Typography variant="body2" color="#1F2937" fontWeight={600} data-id="element-4517">
                        2025-01-01 15:30:00 GMT
                      </Typography>
                      <Typography variant="caption" color="#6B7280" data-id="element-4518">
                        (in 44 minutes)
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start" gap={1.5} data-id="element-4519">
                    <Icon icon="lucide:plug" width={18} color="#6B7280" style={{
                    marginTop: 2
                  }} data-id="element-4520" />
                    <Box data-id="element-4521">
                      <Typography variant="caption" color="#6B7280" display="block" data-id="element-4522">
                        Rate Source
                      </Typography>
                      <Typography variant="body2" color="#1F2937" fontWeight={600} data-id="element-4523">
                        Reuters FX Feed
                      </Typography>
                      <Typography variant="caption" color="#0B7923" fontWeight={600} data-id="element-4524">
                        ✓ Connected
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{
                my: 2,
                borderColor: '#E5E7EB'
              }} data-id="element-4525" />

                <Box mb={3} data-id="element-4526">
                  <Typography variant="caption" color="#6B7280" display="block" mb={1} data-id="element-4527">
                    System Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5} data-id="element-4528">
                    <Typography variant="body2" color="#0B7923" fontWeight={600} data-id="element-4529">
                      Rate Feed Connected ✓
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5} data-id="element-4530">
                    <Typography variant="body2" color="#0B7923" fontWeight={600} data-id="element-4531">
                      Treasury Portal Live ✓
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} data-id="element-4532">
                    <Typography variant="body2" color="#0B7923" fontWeight={600} data-id="element-4533">
                      Settlement Integration ✓
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{
                my: 2,
                borderColor: '#E5E7EB'
              }} data-id="element-4534" />

                <Box data-id="element-4535">
                  <Typography variant="caption" fontWeight={700} color="#1F2937" display="block" mb={1} data-id="element-4536">
                    Daily FX Conversion Limit
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5} data-id="element-4537">
                    <Typography variant="caption" color="#6B7280" data-id="element-4538">
                      Total: $500,000
                    </Typography>
                    <Typography variant="caption" color="#0B7923" fontWeight={600} data-id="element-4539">
                      25% used
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={25} sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#0B7923'
                  },
                  mb: 1
                }} data-id="element-4540" />
                  <Box display="flex" justifyContent="space-between" alignItems="baseline" data-id="element-4541">
                    <Typography variant="body2" color="#1F2937" fontWeight={700} data-id="element-4542">
                      Used: $125,430
                    </Typography>
                    <Typography variant="caption" color="#0B7923" data-id="element-4543">
                      Remaining: $374,570
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" color="#6B7280" display="block" textAlign="center" mt={4} sx={{
                fontSize: '10px'
              }} data-id="element-4544">
                  (Data refreshed: 2025-01-01 15:45:30 UTC)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* DIALOGS */}

      {/* Bulk FX Purchase Dialog */}
      <Dialog open={bulkFxOpen} onClose={() => setBulkFxOpen(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: '8px'
      }
    }} data-id="element-4545">
        <DialogTitle sx={{
        fontWeight: 700,
        color: '#1F2937'
      }} data-id="element-4546">
          Schedule Bulk FX Purchase
        </DialogTitle>
        <DialogContent dividers data-id="element-4547">
          <Grid container spacing={2} data-id="element-4548">
            <Grid item xs={12} sm={6} data-id="element-4549">
              <TextField select fullWidth label="Currency Pair" defaultValue="usdghs" size="small" data-id="element-4550">
                <MenuItem value="usdghs" data-id="element-4551">USD/GHS</MenuItem>
                <MenuItem value="usdxof" data-id="element-4552">USD/XOF</MenuItem>
                <MenuItem value="eurusd" data-id="element-4553">EUR/USD</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} data-id="element-4554">
              <TextField fullWidth label="Amount" size="small" InputProps={{
              startAdornment: <InputAdornment position="start" data-id="element-4556">$</InputAdornment>
            }} data-id="element-4555" />
            </Grid>
            <Grid item xs={12} sm={6} data-id="element-4557">
              <TextField fullWidth type="date" label="Scheduled Date" size="small" InputLabelProps={{
              shrink: true
            }} data-id="element-4558" />
            </Grid>
            <Grid item xs={12} sm={6} data-id="element-4559">
              <TextField select fullWidth label="Priority" defaultValue="normal" size="small" data-id="element-4560">
                <MenuItem value="normal" data-id="element-4561">Normal</MenuItem>
                <MenuItem value="urgent" data-id="element-4562">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} data-id="element-4563">
              <TextField select fullWidth label="Rate Model" defaultValue="daily" size="small" data-id="element-4564">
                <MenuItem value="daily" data-id="element-4565">Daily Fixed</MenuItem>
                <MenuItem value="floating" data-id="element-4566">Floating + Margin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} data-id="element-4567">
              <FormControlLabel control={<Checkbox defaultChecked sx={{
              color: '#0052CC',
              '&.Mui-checked': {
                color: '#0052CC'
              }
            }} data-id="element-4569" />} label="Notify Partners upon execution" data-id="element-4568" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-4570">
          <Button onClick={() => setBulkFxOpen(false)} sx={{
          color: '#6B7280',
          textTransform: 'none'
        }} data-id="element-4571">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => setBulkFxOpen(false)} sx={{
          bgcolor: '#0052CC',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#004099',
            boxShadow: 'none'
          }
        }} data-id="element-4572">
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Rate History Modal */}
      <Dialog open={rateHistoryOpen} onClose={() => setRateHistoryOpen(false)} maxWidth="md" fullWidth PaperProps={{
      sx: {
        borderRadius: '8px'
      }
    }} data-id="element-4573">
        <DialogTitle sx={{
        fontWeight: 700,
        color: '#1F2937'
      }} data-id="element-4574">
          FX Rate History - USD/GHS
        </DialogTitle>
        <DialogContent dividers sx={{
        height: '400px'
      }} data-id="element-4575">
          <ResponsiveContainer width="100%" height="100%" data-id="element-4576">
            <LineChart data={RATE_HISTORY_DATA} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-4577">
              <XAxis dataKey="date" tick={{
              fontSize: 12,
              fill: '#6B7280'
            }} data-id="element-4578" />
              <YAxis domain={['dataMin - 0.2', 'dataMax + 0.2']} tick={{
              fontSize: 12,
              fill: '#6B7280'
            }} data-id="element-4579" />
              <RechartsTooltip data-id="element-4580" />
              <Line type="monotone" dataKey="rate" stroke="#0052CC" strokeWidth={2} dot={false} data-id="element-4581" />
            </LineChart>
          </ResponsiveContainer>
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-4582">
          <Button onClick={() => setRateHistoryOpen(false)} sx={{
          color: '#6B7280',
          textTransform: 'none'
        }} data-id="element-4583">
            Close
          </Button>
          <Button variant="contained" sx={{
          bgcolor: '#F3F5F7',
          color: '#6B7280',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#E5E7EB',
            boxShadow: 'none'
          }
        }} data-id="element-4584">
            Download as CSV
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Margin Update Modal */}
      <Dialog open={bulkMarginOpen} onClose={() => setBulkMarginOpen(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: '8px'
      }
    }} data-id="element-4585">
        <DialogTitle sx={{
        fontWeight: 700,
        color: '#1F2937'
      }} data-id="element-4586">
          Update Corridor Margins
        </DialogTitle>
        <DialogContent dividers data-id="element-4587">
          <Grid container spacing={3} data-id="element-4588">
            <Grid item xs={12} data-id="element-4589">
              <TextField select fullWidth label="Select Corridor" defaultValue="ghana-uk" size="small" data-id="element-4590">
                <MenuItem value="ghana-uk" data-id="element-4591">Ghana-UK</MenuItem>
                <MenuItem value="nigeria-us" data-id="element-4592">Nigeria-US</MenuItem>
                <MenuItem value="ghana-senegal" data-id="element-4593">Ghana-Senegal</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} data-id="element-4594">
              <TextField fullWidth label="Set Margin %" size="small" InputProps={{
              endAdornment: <InputAdornment position="end" data-id="element-4596">%</InputAdornment>
            }} data-id="element-4595" />
            </Grid>
            <Grid item xs={12} data-id="element-4597">
              <Typography variant="subtitle2" mb={1} color="#1F2937" data-id="element-4598">
                Apply to Partners:
              </Typography>
              <Box display="flex" flexDirection="column" data-id="element-4599">
                <FormControlLabel control={<Checkbox defaultChecked sx={{
                color: '#0052CC',
                '&.Mui-checked': {
                  color: '#0052CC'
                }
              }} data-id="element-4601" />} label="Bank Ghana Ltd" data-id="element-4600" />
                <FormControlLabel control={<Checkbox defaultChecked sx={{
                color: '#0052CC',
                '&.Mui-checked': {
                  color: '#0052CC'
                }
              }} data-id="element-4603" />} label="MTN Mobile Money" data-id="element-4602" />
                <FormControlLabel control={<Checkbox defaultChecked sx={{
                color: '#0052CC',
                '&.Mui-checked': {
                  color: '#0052CC'
                }
              }} data-id="element-4605" />} label="Guaranty Trust Bank" data-id="element-4604" />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-4606">
          <Button onClick={() => setBulkMarginOpen(false)} sx={{
          color: '#6B7280',
          textTransform: 'none'
        }} data-id="element-4607">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => setBulkMarginOpen(false)} sx={{
          bgcolor: '#0052CC',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#004099',
            boxShadow: 'none'
          }
        }} data-id="element-4608">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}